import { isEqual } from "lodash-es";

import type { PolygonData_api } from "@api";
import { getPolygonsDataOptions, getPolygonsDirectoryOptions, PolygonsAttributeType_api } from "@api";
import { makeCacheBustingQueryParam } from "@framework/utils/queryUtils";
import type {
    CustomDataProviderImplementation,
    FetchDataParams,
} from "@modules/_shared/DataProviderFramework/interfacesAndTypes/customDataProviderImplementation";
import type { SetupBindingsContext } from "@modules/_shared/DataProviderFramework/interfacesAndTypes/customSettingsHandler";
import { Setting } from "@modules/_shared/DataProviderFramework/settings/settingsDefinitions";

import type { MakeSettingTypesMap } from "../../interfacesAndTypes/utils";
import {
    getAvailableEnsembleIdentsForField,
    getAvailableRealizationsForEnsembleIdent,
} from "../dependencyFunctions/sharedSettingUpdaterFunctions";

const realizationPolygonsSettings = [
    Setting.ENSEMBLE,
    Setting.REALIZATION,
    Setting.POLYGONS_ATTRIBUTE,
    Setting.SURFACE_NAME,
    Setting.POLYGON_VISUALIZATION,
] as const;
const ALLOWED_SURFACE_TYPES_FROM_API = [PolygonsAttributeType_api.FAULT_LINES];
export type FaultPolygonsSettings = typeof realizationPolygonsSettings;
type SettingsWithTypes = MakeSettingTypesMap<FaultPolygonsSettings>;

export type FaultPolygonsData = PolygonData_api[];
export class FaultPolygonsProvider implements CustomDataProviderImplementation<
    FaultPolygonsSettings,
    FaultPolygonsData
> {
    settings = realizationPolygonsSettings;

    getDefaultName(): string {
        return "Fault Polygons";
    }

    doSettingsChangesRequireDataRefetch(prevSettings: SettingsWithTypes, newSettings: SettingsWithTypes): boolean {
        return !isEqual(prevSettings, newSettings);
    }

    setupBindings({ setting, makeSharedResult, queryClient }: SetupBindingsContext<FaultPolygonsSettings>) {
        setting(Setting.ENSEMBLE).bindValueConstraints({
            read(read) {
                return {
                    fieldIdentifier: read.globalSetting("fieldId"),
                    ensembles: read.globalSetting("ensembles"),
                };
            },
            resolve({ fieldIdentifier, ensembles }) {
                return getAvailableEnsembleIdentsForField(fieldIdentifier, ensembles);
            },
        });

        setting(Setting.REALIZATION).bindValueConstraints({
            read(read) {
                return {
                    ensembleIdent: read.localSetting(Setting.ENSEMBLE),
                    realizationFilterFunction: read.globalSetting("realizationFilterFunction"),
                };
            },
            resolve({ ensembleIdent, realizationFilterFunction }) {
                return getAvailableRealizationsForEnsembleIdent(ensembleIdent, realizationFilterFunction);
            },
        });

        const polygonsMetadataDep = makeSharedResult({
            debugName: "PolygonsMetadata",
            read(read) {
                return {
                    ensembleIdent: read.localSetting(Setting.ENSEMBLE),
                };
            },
            async resolve({ ensembleIdent }, { abortSignal }) {
                if (!ensembleIdent) {
                    return null;
                }

                return await queryClient.fetchQuery({
                    ...getPolygonsDirectoryOptions({
                        query: {
                            case_uuid: ensembleIdent.getCaseUuid(),
                            ensemble_name: ensembleIdent.getEnsembleName(),
                            ...makeCacheBustingQueryParam(ensembleIdent),
                        },
                        signal: abortSignal,
                    }),
                });
            },
        });

        setting(Setting.POLYGONS_ATTRIBUTE).bindValueConstraints({
            read(read) {
                return {
                    data: read.sharedResult(polygonsMetadataDep),
                };
            },
            resolve({ data }) {
                if (!data) {
                    return [];
                }
                const filteredPolygonsMeta = data.filter((polygonsMeta) =>
                    ALLOWED_SURFACE_TYPES_FROM_API.includes(polygonsMeta.attribute_type),
                );
                return [...new Set(filteredPolygonsMeta.map((polygonsMeta) => polygonsMeta.attribute_name))];
            },
        });

        setting(Setting.SURFACE_NAME).bindValueConstraints({
            read(read) {
                return {
                    attribute: read.localSetting(Setting.POLYGONS_ATTRIBUTE),
                    data: read.sharedResult(polygonsMetadataDep),
                };
            },
            resolve({ attribute, data }) {
                if (!attribute || !data) {
                    return [];
                }
                return [
                    ...new Set(
                        data.filter((polygonsMeta) => polygonsMeta.attribute_name === attribute).map((el) => el.name),
                    ),
                ];
            },
        });
    }

    fetchData({
        getSetting,
        fetchQuery,
    }: FetchDataParams<FaultPolygonsSettings, FaultPolygonsData>): Promise<PolygonData_api[]> {
        const ensembleIdent = getSetting(Setting.ENSEMBLE);
        const realizationNum = getSetting(Setting.REALIZATION);
        const surfaceName = getSetting(Setting.SURFACE_NAME);
        const polygonsAttribute = getSetting(Setting.POLYGONS_ATTRIBUTE);

        const queryOptions = getPolygonsDataOptions({
            query: {
                case_uuid: ensembleIdent?.getCaseUuid() ?? "",
                ensemble_name: ensembleIdent?.getEnsembleName() ?? "",
                realization_num: realizationNum ?? 0,
                name: surfaceName ?? "",
                attribute: polygonsAttribute ?? "",
                ...makeCacheBustingQueryParam(ensembleIdent),
            },
        });

        const promise = fetchQuery({
            ...queryOptions,
        });

        return promise;
    }
}
